import React, { useState } from "react";
import cn from "classnames";
import { noop } from "lib/utils";
import { isToday, isTomorrow } from "date-fns";
import { TIME_FORMAT } from "models/DateTime";
import A11yElement from "components/helpers/A11yElement";
import useLayout from "hooks/useLayout";
import DatePopup from "components/datetime/DatePopup";
import { ReactComponent as CalendarIcon } from "static/images/calendar.svg";
import "styles/components/date-time-input.scss";
import t from "lib/i18n";

const DateTimeInput = ({
  dateTime,
  isError = false,
  isSeason = false,
  isOther = false,
  mobileLabel,
  onChange = noop,
  onModalRequest = noop,
  precedesReturn = false,
  isReturn = false,
  short = false,
  maxDate,
  minDate,
  disableTimePicker = false,
  rightAlignPopup = false,
}) => {
  const [isOpen, setOpen] = useState(false);
  const { isMobile } = useLayout();

  const handleClick = () => {
    setOpen(!isOpen);

    if (isMobile) {
      onModalRequest();
    }
  };

  const onHide = () => {
    setOpen(false);
  };

  let dateFormatted = dateTime.format("d MMM");
  let dateFormattedMobile = dateTime.format("EEEE d MMMM");

  if (isToday(dateTime.toDate())) {
    dateFormatted = dateFormattedMobile = "Today";
  } else if (isTomorrow(dateTime.toDate())) {
    dateFormatted = dateFormattedMobile = "Tomorrow";
  }

  const timeFormatted = dateTime.isDepartingNow()
    ? "Depart now"
    : dateTime.format(TIME_FORMAT);

  let mainText = isMobile ? mobileLabel : dateFormatted;
  let subText = isMobile ? dateFormattedMobile : timeFormatted;
  let rightText = timeFormatted;
  let ariaPrefix = isReturn
    ? t("tickets.date.return.aria")
    : t("tickets.date.depart.aria");

  if (isSeason || isOther) {
    mainText = isReturn ? "Ending" : "Starting";
    subText = dateFormatted;
    rightText = "";
    ariaPrefix = mainText;
  }

  const ariaLabel = dateTime.isDepartingNow()
    ? `${mainText} ${subText}`
    : `${ariaPrefix} ${mainText} ${subText}`;

  return (
    <A11yElement
      role="button"
      className={cn("otrl-jp__date-input", {
        "otrl-jp__date-input--opened": isOpen && !isMobile,
        "otrl-jp__date-input--precedes-return": precedesReturn,
        "otrl-jp__date-input--short": short,
        "otrl-jp__date-input--popup-right": rightAlignPopup,
        "otrl-jp__date-input--error": isError,
      })}
      onAction={handleClick}
      aria-label={ariaLabel}
    >
      <div className="otrl-jp__date-input__container">
        <CalendarIcon className="otrl-jp__date-input__icon" />
        <div className="otrl-jp__date-input__input">
          <span className="otrl-jp__date-input__date">{mainText}</span>
          <div className="otrl-jp__date-input__second-row">
            <span className="otrl-jp__date-input__time">{subText}</span>
            {!disableTimePicker && isMobile && (
              <span className="otrl-jp__date-input__time">{rightText}</span>
            )}
          </div>
        </div>
      </div>
      {isOpen && !isMobile && (
        <DatePopup
          dateTime={dateTime}
          onChange={onChange}
          onHide={onHide}
          isReturn={isReturn}
          isSeason={isSeason}
          isOther={isOther}
          maxDate={maxDate}
          minDate={minDate}
        />
      )}
    </A11yElement>
  );
};

export default DateTimeInput;
